/**
 * Vocabulary: readers see this feature as "owner reviews"; code, routes, DealHunt paths and
 * analytics events keep the historical "owner note" name on purpose — do not rename them.
 *
 * Turning "the one I own" into a `DeclaredProduct` — written once for the two capture surfaces
 * (the category band, the guide's chooser). A declaration makes ONE resolve: it carries the
 * scores, the spec sheet, the lab id AND the owner aggregate, so nothing else has to ask again
 * (Directive cae90548). What is surface-local stays with the surface: the category grid's
 * shortcut (a product already in the grid needs no resolve), the guide's pick context, the
 * `declared` event and its `location`, the error copy.
 *
 * Not to be confused with `declared.ts`, the store (localStorage + context) the result lands in.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { fetchOwnerNotes, resolveDealHuntPage } from 'lib/dealhunt/client'
import type { DealHuntPageData, DealHuntSuggestHit } from 'lib/dealhunt/types'
import { brandedTitle, dealHuntProductHref, productSlug } from 'lib/dealhunt/url'
import { ownersFromEnrichment, ownersFromList, type OwnersSummary } from './band-copy'
import { numbersFromProductLine } from './compare-source'
import type { DeclaredProduct, DeclareOptions } from './declared'
import type { DeltaNumbers } from './deltas'

/** What a declaration needs before the resolve fills in the numbers. */
export interface DeclareSeed {
  /** DealHunt fullSlug — what the resolve is keyed on; null = nothing to resolve. */
  slug: string | null
  brand: string | null
  brandSlug: string | null
  name: string
  imageUrl: string | null
  priceCents: number | null
  sgScore: number | null
  /** Known up front on a shelf row; the resolve's canonical id wins when it answers. */
  nodeId?: string | null
}

/** Owner counts for one node: what a resolve (or the public list) said about it. */
export interface NodeOwners {
  nodeId: string
  owners: OwnersSummary | null
}

/** A typeahead hit as a seed — the same index as the header search. */
export function seedFromHit(hit: DealHuntSuggestHit): DeclareSeed {
  return {
    slug: hit.fullSlug,
    brand: hit.brand,
    brandSlug: hit.brandSlug ?? null,
    name: brandedTitle(hit.brand, hit.canonicalName || hit.title),
    imageUrl: hit.cloudinaryImageUrl || hit.imageUrl,
    priceCents: hit.currentPriceCents,
    sgScore: hit.sgScore ?? null,
  }
}

/** The seed's own numbers: all a suggest hit or a shelf row knows before the resolve. */
export function seedNumbers(seed: DeclareSeed): DeltaNumbers {
  return {
    axes: null,
    batteryHours: null,
    priceCents: seed.priceCents,
    sgScore: seed.sgScore,
    clearbuyProductId: null,
    compareSpecs: null,
  }
}

/** The stored declaration for a seed, its node id and the best numbers in hand. */
export function declaredFromSeed(
  seed: DeclareSeed,
  nodeId: string | null,
  numbers: DeltaNumbers,
  trailingSlash: boolean
): DeclaredProduct {
  return {
    nodeId,
    slug: seed.slug,
    href: seed.slug
      ? dealHuntProductHref(seed.slug, seed.brand, trailingSlash, seed.brandSlug)
      : null,
    name: seed.name,
    imageUrl: seed.imageUrl,
    ...numbers,
    declaredAt: Date.now(),
  }
}

/** How long a resolve answer is reused, and how many are kept — a search list's worth. */
const RESOLVE_MAX_AGE_MS = 5 * 60_000
const RESOLVE_CACHE_MAX = 8
const resolveCache = new Map<string, { at: number; page: Promise<DealHuntPageData | null> }>()

/** What the resolve is asked for — the cache key, so a hover and the click on it share one. */
function resolvePath(seed: DeclareSeed): string | null {
  return seed.slug ? productSlug(seed.brand, seed.slug, seed.brandSlug) : null
}

/** The page for a path: the request already made (in flight or answered), else a new one. A
 *  failure is dropped from the cache, so the next ask tries again. */
function cachedResolve(path: string): Promise<DealHuntPageData | null> {
  const held = resolveCache.get(path)
  if (held && Date.now() - held.at < RESOLVE_MAX_AGE_MS) {
    return held.page
  }
  const entry = { at: Date.now(), page: resolveDealHuntPage([path]) }
  resolveCache.set(path, entry)
  // Also the handler for a prefetch nobody picked: no unhandled rejection.
  entry.page.catch(() => {
    if (resolveCache.get(path) === entry) {
      resolveCache.delete(path)
    }
  })
  if (resolveCache.size > RESOLVE_CACHE_MAX) {
    const [oldest] = resolveCache.keys()
    resolveCache.delete(oldest)
  }
  return entry.page
}

/**
 * Start the resolve for a search result the reader is resting on (hover, arrow keys), so the
 * pick that usually follows finds it in flight or answered. Each resolve is ~30 DB queries: the
 * typeahead only calls this once the highlight stays put (`useOwnTypeahead`'s `onPreview`).
 */
export function prefetchDeclaration(seed: DeclareSeed): void {
  const path = resolvePath(seed)
  if (path) {
    void cachedResolve(path)
  }
}

/** Test seam: forget every held resolve. */
export function clearDeclarationCache(): void {
  resolveCache.clear()
}

/**
 * The one resolve: node id, numbers and owner counts for a seed. `owners` is null when the
 * seed has no product page (the deltas still work, the note needs a page). Rejects when the
 * lookup itself failed — the caller says so in its own words.
 */
export async function resolveDeclaration(
  seed: DeclareSeed,
  trailingSlash: boolean
): Promise<{ declared: DeclaredProduct; owners: NodeOwners | null }> {
  let nodeId = seed.nodeId ?? null
  let numbers = seedNumbers(seed)
  let owners: NodeOwners | null = null
  const path = resolvePath(seed)
  if (path) {
    const page = await cachedResolve(path)
    if (page?.kind === 'product') {
      nodeId = page.node.id
      numbers = numbersFromProductLine(page, numbers)
      owners = { nodeId, owners: ownersFromEnrichment(page.enrichment?.owners) }
    }
  }
  return { declared: declaredFromSeed(seed, nodeId, numbers, trailingSlash), owners }
}

/**
 * Owner counts for the strip's question when the page does not already hold them (`known`:
 * the category grid's card, a guide pick's build-time context): what the declaration's own
 * resolve said, else ONE cheap public-list read for a declaration carried in from another
 * page. Never a second `resolveDealHuntPage` (Directive cae90548).
 */
export function useDeclaredOwners(
  nodeId: string | null,
  known: boolean
): { owners: OwnersSummary | null; setResolved: (owners: NodeOwners | null) => void } {
  const [resolved, setResolved] = useState<NodeOwners | null>(null)
  useEffect(() => {
    if (!nodeId || known || resolved?.nodeId === nodeId) {
      return undefined
    }
    const ctrl = new AbortController()
    fetchOwnerNotes(nodeId, 1, 'default', ctrl.signal).then(
      list => setResolved({ nodeId, owners: ownersFromList(list) }),
      () => undefined
    )
    return () => ctrl.abort()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once per declared product
  }, [nodeId, known])
  return {
    owners: resolved && resolved.nodeId === nodeId ? resolved.owners : null,
    setResolved,
  }
}

export interface ResolvedDeclaration {
  declared: DeclaredProduct
  owners: NodeOwners | null
}

/**
 * A pick shows at once (2026-09-25): the strip draws from what the search result already knows
 * (name, image, price, SG score) while the one resolve runs (~2 s on prod), instead of the reader
 * waiting on "Looking it up…". The provisional declaration has no node id — so no ask, no own
 * note, no `declared` event until the resolve answers — and is NOT persisted: a reader who leaves
 * mid-lookup must not carry a node-less declaration (= no ask, ever) to the next page.
 *
 * `start` resolves to the answer for the caller to declare — together with its owner counts, in
 * one tick — or null when a later pick, a clear (`cancel`) or an unmount superseded it. A failed
 * lookup puts the page back to undeclared (picks are only made from there) and rethrows, so the
 * surface says so in its own words.
 */
export function usePendingDeclare(
  declare: (product: DeclaredProduct | null, options?: DeclareOptions) => void,
  trailingSlash: boolean
): {
  start: (seed: DeclareSeed) => Promise<ResolvedDeclaration | null>
  cancel: () => void
} {
  const seq = useRef(0)
  useEffect(
    () => () => {
      seq.current += 1
    },
    []
  )
  const cancel = useCallback((): void => {
    seq.current += 1
  }, [])
  const start = useCallback(
    (seed: DeclareSeed) => startPendingDeclare(seq, declare, seed, trailingSlash),
    [declare, trailingSlash]
  )
  return { start, cancel }
}

/** `usePendingDeclare`'s `start`, outside React: `seq` is its supersede counter. */
export async function startPendingDeclare(
  seq: { current: number },
  declare: (product: DeclaredProduct | null, options?: DeclareOptions) => void,
  seed: DeclareSeed,
  trailingSlash: boolean
): Promise<ResolvedDeclaration | null> {
  seq.current += 1
  const mine = seq.current
  declare(declaredFromSeed(seed, null, seedNumbers(seed), trailingSlash), { persist: false })
  try {
    const resolved = await resolveDeclaration(seed, trailingSlash)
    return seq.current === mine ? resolved : null
  } catch (err) {
    if (seq.current !== mine) {
      return null
    }
    declare(null)
    throw err
  }
}
